import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, Download, Upload, Users as UsersIcon, UserX, UserMinus, Copy, FileUp, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken, formatTL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdvanceModal } from "@/components/AdvanceTab";
import { CIKIS_SEBEP_LABELS } from "./personnel/constants";
import PersonnelModal from "./personnel/PersonnelModal";
import KidemSimulationModal from "./personnel/KidemSimulationModal";
import BulkImportModal from "./personnel/BulkImportModal";
import DuplicateScanModal from "./personnel/DuplicateScanModal";
import { MissingIbanPanel } from "./personnel/MissingIbanPanel";
import { InvalidTcPanel } from "./personnel/InvalidTcPanel";
import { MissingMealCardPanel } from "./personnel/MissingMealCardPanel";
import { PendingSalaryPanel } from "./personnel/PendingSalaryPanel";
import { MissingDocsPanel } from "./personnel/MissingDocsPanel";
import { BulkDocsModal } from "./personnel/BulkDocsModal";
import ImportResultModal from "./personnel/ImportResultModal";
import BulkPasifModal from "./personnel/BulkPasifModal";
import { BulkRaiseModal } from "./personnel/BulkRaiseModal";
import ExitPdfModal from "./personnel/ExitPdfModal";
import PersonnelTable from "./personnel/PersonnelTable";
import PersonnelMobileCards from "./personnel/PersonnelMobileCards";
import PositionTemplateManager from "./personnel/PositionTemplateManager";
import { BulkStatementButtons } from "./personnel/BulkStatementButtons";
import { OrphanAccountsPanel } from "./users/OrphanAccountsPanel";
import { EkstreFilter } from "./personnel/EkstreFilter";

export default function PersonnelTab({ yil, ay, onChange, refreshKey }) {
  const { canSeeMoney, isAdmin, canSeeKidem, isMuhasebeci, role, canManageStatements } = useAuth();
  const [ibanKey, setIbanKey] = useState(0);
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [bulkDocs, setBulkDocs] = useState(false);
  const [docsKey, setDocsKey] = useState(0);
  const [docsIndex, setDocsIndex] = useState({});
  useEffect(() => {
    if (isAdmin || isMuhasebeci) api.get("/personnel/documents-index").then((r) => setDocsIndex(r.data.index || {})).catch(() => {});
  }, [docsKey, isAdmin, isMuhasebeci]);
  const [showPtmModal, setShowPtmModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kidemModalPerson, setKidemModalPerson] = useState(null);
  const [quickAdvancePerson, setQuickAdvancePerson] = useState(null);
  const [view, setView] = useState("aktif"); // 'aktif' | 'pasif'
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [dupScanOpen, setDupScanOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkPasifOpen, setBulkPasifOpen] = useState(false);
  const [exitPdfPerson, setExitPdfPerson] = useState(null);
  const [ekstreFilter, setEkstreFilter] = useState("all");
  const [stViews, setStViews] = useState(null);
  useEffect(() => {
    if (canManageStatements) api.get("/prim-statement/views").then((r) => setStViews(r.data)).catch(() => setStViews(null));
  }, [canManageStatements, refreshKey]);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/personnel");
      setList(r.data);
    } catch (e) {
      toast.error("Personel listesi yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu personeli silmek istediğinize emin misiniz? Tüm avans ve bordro kayıtları da silinecek.")) return;
    try {
      await api.delete(`/personnel/${id}`);
      toast.success("Personel silindi");
      load();
      onChange?.();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const activeList = useMemo(() => list.filter((p) => (view === "aktif" ? p.aktif : !p.aktif)), [list, view]);
  const ekstreCounts = useMemo(() => {
    const open = activeList.filter((p) => p.ekstre_acik).length;
    return { all: activeList.length, open, closed: activeList.length - open };
  }, [activeList]);
  const filtered = useMemo(() => activeList.filter((p) =>
    (ekstreFilter === "all" || (ekstreFilter === "open") === !!p.ekstre_acik) &&
    [p.ad_soyad, p.sicil_no, p.pozisyon].some((f) =>
      (f || "").toLowerCase().includes(query.toLowerCase())
    )
  ), [activeList, query, ekstreFilter]);

  useEffect(() => { setSelected(new Set()); }, [view]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  };

  const activeCount = useMemo(() => list.filter((p) => p.aktif).length, [list]);
  const pasifCount = list.length - activeCount;

  const handleExport = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/personnel/export?include=${view}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      let fname = `personel_listesi_${view}.xlsx`;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      if (m) fname = m[1];
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch {
      toast.error("Dışa aktarım başarısız");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 md:p-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div>
          <h2 className="font-display text-lg md:text-xl font-bold text-slate-900">
            Personel Listesi
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">
            {activeCount} aktif · {pasifCount} pasif · toplam {list.length} kayıt
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
          <div className="relative flex-1 md:flex-none min-w-[160px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="personnel-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ara..."
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>

          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              data-testid="view-aktif"
              onClick={() => setView("aktif")}
              className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${view === "aktif" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <UsersIcon size={13} /> Aktif
              <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded ${view === "aktif" ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{activeCount}</span>
            </button>
            <button
              data-testid="view-pasif"
              onClick={() => setView("pasif")}
              className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${view === "pasif" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <UserX size={13} /> Pasif
              <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded ${view === "pasif" ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{pasifCount}</span>
            </button>
          </div>

          {canManageStatements && <EkstreFilter value={ekstreFilter} onChange={setEkstreFilter} counts={ekstreCounts} />}

          <button
            data-testid="export-personnel"
            onClick={handleExport}
            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0"
            title="Mevcut listeyi Excel olarak indir"
          >
            <Download size={14} /> Excel
          </button>

          {isAdmin && (
            <button data-testid="open-bulk-raise" onClick={() => setRaiseOpen(true)}
              className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0">
              <TrendingUp size={14} /> Toplu Zam
            </button>
          )}
          {raiseOpen && <BulkRaiseModal selectedIds={Array.from(selected)} onClose={() => setRaiseOpen(false)}
            onDone={() => { setRaiseOpen(false); load(); }} />}

          {isAdmin && (
            <button
              data-testid="open-import-modal"
              onClick={() => setImportModalOpen(true)}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0"
            >
              <Upload size={14} /> Toplu Ekle
            </button>
          )}

          {(isAdmin || isMuhasebeci) && (
            <button
              data-testid="open-ptm-modal"
              onClick={() => setShowPtmModal(true)}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0"
              title="Pozisyon şablonlarını yönet, drift düzelt"
            >
              <UsersIcon size={14} /> Pozisyon Şablonları
            </button>
          )}

          {(isAdmin || isMuhasebeci) && (
            <button data-testid="open-bulk-docs" onClick={() => setBulkDocs(true)}
              className="flex items-center gap-1.5 bg-white hover:bg-rose-50 border border-rose-300 text-rose-800 px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0"
              title="SGK bildirgelerini toplu yükle, TC no ile eşleştir">
              <FileUp size={14} /> Toplu Bildirge
            </button>
          )}

          <button
            data-testid="open-duplicate-scan"
            onClick={() => setDupScanOpen(true)}
            className="flex items-center gap-1.5 bg-white hover:bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0"
            title="Aynı TC kimlik no ve ad soyada sahip kayıtları bul"
          >
            <Copy size={14} /> Mükerrer Tarama
          </button>

          <button
            data-testid="add-personnel-button"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-lg font-semibold text-xs transition-colors shrink-0"
          >
            <Plus size={14} /> Personel Ekle
          </button>
        </div>
      </div>

      <InvalidTcPanel list={list} onEdit={(p) => { setEditing(p); setModalOpen(true); }} />
      {(isAdmin || isMuhasebeci) && <PendingSalaryPanel refreshKey={list} isAdmin={isAdmin} onEdit={(p) => { setEditing(p); setModalOpen(true); }} />}
      {(isAdmin || isMuhasebeci) && <MissingDocsPanel refreshKey={`${docsKey}-${list.length}`} />}
      {bulkDocs && <BulkDocsModal onClose={() => setBulkDocs(false)} onDone={() => setDocsKey((k) => k + 1)} />}
      {(isAdmin || isMuhasebeci) && <MissingMealCardPanel refreshKey={list.length} />}
      {canManageStatements && <OrphanAccountsPanel />}
      {(isAdmin || isMuhasebeci) && <MissingIbanPanel refreshKey={`${ibanKey}-${list.length}`} onSaved={() => setIbanKey((k) => k + 1)} />}

      {view === "aktif" && (isAdmin || canManageStatements) && selected.size > 0 && (
        <div
          data-testid="bulk-actions-bar"
          className="px-4 md:px-5 py-2.5 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center gap-3 text-xs text-amber-900"
        >
          <span className="font-semibold">
            <strong>{selected.size}</strong> personel seçildi
          </span>
          <button
            onClick={() => setSelected(new Set())}
            className="text-amber-800 hover:underline"
          >Seçimi Temizle</button>
          <div className="ml-auto flex items-center gap-2">
            {canManageStatements && <BulkStatementButtons ids={Array.from(selected)} onDone={load} />}
            {isAdmin && <button
              data-testid="bulk-pasif-button"
              onClick={() => setBulkPasifOpen(true)}
              className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg font-bold uppercase tracking-wider text-[11px]"
            >
              <UserMinus size={13} /> Toplu Pasif Yap ({selected.size})
            </button>}
          </div>
        </div>
      )}

      <PersonnelMobileCards
        rows={filtered}
        loading={loading}
        view={view}
        isAdmin={isAdmin}
        canSeeKidem={canSeeKidem}
        canSeeMoney={canSeeMoney}
        hideCCRateBadge={role === "muhasebeci"}
        selected={selected}
        onToggleSelect={toggleSelect}
        onEdit={(p) => { setEditing(p); setModalOpen(true); }}
        onDelete={handleDelete}
        onAdvance={setQuickAdvancePerson}
        onKidemSim={setKidemModalPerson}
        onExitPdf={setExitPdfPerson}
        docsIndex={(isAdmin || isMuhasebeci) ? docsIndex : null}
        onDocsChanged={() => setDocsKey((k) => k + 1)}
      />

      <PersonnelTable
        rows={filtered}
        loading={loading}
        view={view}
        isAdmin={isAdmin}
        canManageStatements={canManageStatements}
        statementViews={stViews}
        onStatementChanged={(id, v) => setList((l) => l.map((x) => (x.id === id ? { ...x, ekstre_acik: v } : x)))}
        canSeeKidem={canSeeKidem}
        canSeeMoney={canSeeMoney}
        hideCCRateBadge={role === "muhasebeci"}
        selected={selected}
        allSelected={allSelected}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onEdit={(p) => { setEditing(p); setModalOpen(true); }}
        onDelete={handleDelete}
        onAdvance={setQuickAdvancePerson}
        onKidemSim={setKidemModalPerson}
        onExitPdf={setExitPdfPerson}
        docsIndex={(isAdmin || isMuhasebeci) ? docsIndex : null}
        onDocsChanged={() => setDocsKey((k) => k + 1)}
      />

      {modalOpen && (
        <PersonnelModal
          editing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); onChange?.(); }}
        />
      )}

      {kidemModalPerson && (
        <KidemSimulationModal
          person={kidemModalPerson}
          onClose={() => setKidemModalPerson(null)}
        />
      )}

      {quickAdvancePerson && (
        <AdvanceModal
          personnel={[quickAdvancePerson]}
          lockedPersonnelId={quickAdvancePerson.id}
          onClose={() => setQuickAdvancePerson(null)}
          onSaved={() => {
            setQuickAdvancePerson(null);
            toast.success(`${quickAdvancePerson.ad_soyad} için avans eklendi`);
            onChange?.();
          }}
        />
      )}

      {dupScanOpen && (
        <DuplicateScanModal canDelete={isAdmin} onClose={() => setDupScanOpen(false)} onChanged={() => { load(); onChange?.(); }} />
      )}
      {importModalOpen && (
        <BulkImportModal
          onClose={() => setImportModalOpen(false)}
          onDone={(res) => {
            setImportModalOpen(false);
            setImportResult(res);
            load();
            onChange?.();
          }}
        />
      )}

      {importResult && (
        <ImportResultModal
          result={importResult}
          onClose={() => setImportResult(null)}
        />
      )}

      {bulkPasifOpen && (
        <BulkPasifModal
          ids={Array.from(selected)}
          onClose={() => setBulkPasifOpen(false)}
          onDone={() => {
            setBulkPasifOpen(false);
            setSelected(new Set());
            load();
            onChange?.();
          }}
        />
      )}

      {exitPdfPerson && (
        <ExitPdfModal
          person={exitPdfPerson}
          onClose={() => setExitPdfPerson(null)}
        />
      )}

      {showPtmModal && (
        <PositionTemplateManager
          yil={yil} ay={ay}
          onClose={() => setShowPtmModal(false)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}
